import { computed, effectScope, ref } from 'vue'
import { useWebSocket } from '@vueuse/core'
import {
  reportSocketDisconnected,
  reportSocketEmitError,
  reportSocketError,
  reportSocketMessageParseError,
  reportSocketReconnectFailed,
} from '@/utils/monitor'

const MAX_RECONNECT_DELAY = 15000

class BaseSocketClient {
  status = ref('CLOSED')

  wsRef = ref()

  url = ref('')

  events = {}

  sends = {}

  subIds = {}

  subPayloads = {}

  resubscribeTasks = {}

  scope = null

  sendRaw = null

  openRaw = null

  closeRaw = null

  isManualClose = false

  hasOpened = false

  constructor ({ wsUrl, reconnectEventName, logPrefix = 'socket' }) {
    this.baseUrl = wsUrl
    this.reconnectEventName = reconnectEventName
    this.logPrefix = logPrefix
    this.init()
  }

  get ws () {
    return this.wsRef.value
  }

  set ws (value) {
    this.wsRef.value = value
  }

  get eventsKeys () {
    return Object.keys(this.events)
  }

  init () {
    this.scope = effectScope()
    this.scope.run(() => {
      const socket = useWebSocket(computed(() => this.url.value || undefined), {
        immediate: false,
        autoClose: false,
        autoReconnect: {
          retries: 1000000,
          delay: retryCount => Math.min(1000 * (2 ** Math.max(retryCount - 1, 0)), MAX_RECONNECT_DELAY),
          onFailed: () => {
            console.log(`${this.logPrefix} reconnect failed`)
            // 重连次数耗尽后统一上报，帮助定位整条 WS 链路不可用的问题。
            reportSocketReconnectFailed({ client: this })
          }
        },
        onConnected: (ws) => {
          this.ws = ws
          this.status.value = 'OPEN'
          const isReconnect = this.hasOpened && !this.isManualClose
          this.hasOpened = true
          this.flushSends()
          if (isReconnect) {
            this.replaySubscriptions()
            this.replayResubscribeTasks()
            eventBus.emit(this.reconnectEventName)
          }
        },
        onDisconnected: (_, event) => {
          this.status.value = 'CLOSED'
          if (!this.isManualClose) {
            console.log(`${this.logPrefix} disconnected`, event?.code)
            // 只在非手动关闭时上报，避免页面卸载/登出时产生噪音。
            reportSocketDisconnected({ client: this, event })
          }
        },
        onError: (_, event) => {
          console.log(`${this.logPrefix} error`, event)
          // 浏览器底层 error 事件统一上报成 warning。
          reportSocketError({ client: this, event })
        },
        onMessage: async (_, event) => {
          await this.handleMessage(event.data)
        }
      })

      this.status = socket.status
      this.wsRef = socket.ws
      this.sendRaw = socket.send
      this.openRaw = socket.open
      this.closeRaw = socket.close
    })
  }

  buildUrl () {
    const userStore = useUserStore()
    return userStore.token ? this.baseUrl + userStore.token : this.baseUrl
  }

  connect () {
    this.isManualClose = false
    this.url.value = this.buildUrl()

    if (this.status.value !== 'OPEN' && this.status.value !== 'CONNECTING') {
      this.openRaw?.()
    }

    return this
  }

  hasEvent (key) {
    return this.eventsKeys.includes(key)
  }

  isTopicMatched (eventKey, topic) {
    return eventKey === topic || eventKey.startsWith(`${topic}@`)
  }

  async handleMessage (rawData) {
    try {
      let data = null
      if (!isJson(rawData)) {
        data = await praseWsBlob(rawData)
      } else {
        data = rawData
      }

      data = isJsonString(data) ? JSON.parse(data) : data
      if (!isObject(data)) {
        return
      }

      if (Object.keys(data).includes('invalidParameter')) {
        return
      }

      if (Object.keys(data).includes('ping')) {
        this.sendNow({ pong: data.ping })
        return
      }

      const topic = data?.topic
      if (!topic) {
        return
      }

      this.eventsKeys
        .filter(key => this.isTopicMatched(key, topic))
        .forEach(key => {
          this.emit(key, fixTime(data))
        })
    } catch (error) {
      console.log(`${this.logPrefix} message parse error`, error)
      // 原始消息解析失败通常意味着协议或数据流异常，直接进入监控。
      reportSocketMessageParseError({ client: this, error, rawData })
    }
  }

  normalizeSendData (data) {
    const nextData = { ...data }
    const topic = nextData?.topic
    const cmd = nextData?.cmd

    if (cmd === 'sub' && topic) {
      nextData.id = nextData.id ?? randomString()
      this.subIds[topic] = nextData.id
      this.subPayloads[topic] = {
        ...nextData,
        data: nextData.data ?? {}
      }
    }

    if (cmd === 'unsub' && topic) {
      nextData.id = this.subIds[topic] ?? nextData.id ?? randomString()
      delete this.subIds[topic]
      delete this.subPayloads[topic]
    }

    return nextData
  }

  sendNow (data) {
    const nextData = this.normalizeSendData(data)
    return this.sendRaw?.(JSON.stringify(nextData))
  }

  send (data) {
    const sendId = data?.cmd && data?.topic ? `${data.cmd}:${data.topic}` : (data?.id ?? randomString())
    this.sends[sendId] = () => this.sendNow(data)
    this.flushSends()
  }

  flushSends () {
    if (this.status.value !== 'OPEN') {
      return
    }

    for (const key in this.sends) {
      this.sends[key]()
    }
    this.sends = {}
  }

  on (key, callBack) {
    if (!isFunc(callBack)) {
      throw new Error('callBack 非函数')
    }
    this.events[key] = callBack
  }

  off (key) {
    delete this.events[key]
  }

  emit (key, data) {
    try {
      if (!key || !this.events[key]) {
        return
      }
      this.events[key](data)
    } catch (error) {
      console.log('emit err', key, error)
      // 某个订阅回调内部报错时，连同 topic 一起上报，方便反查是哪条链路。
      reportSocketEmitError({ client: this, eventKey: key, error })
    }
  }

  registerResubscribe (key, task) {
    if (!isFunc(task)) {
      throw new Error('task 非函数')
    }
    this.resubscribeTasks[key] = task
  }

  unregisterResubscribe (key) {
    delete this.resubscribeTasks[key]
  }

  replaySubscriptions () {
    const payloads = Object.values(this.subPayloads)
    this.subIds = {}
    payloads.forEach((payload) => {
      this.sendNow({
        ...payload,
        id: randomString()
      })
    })
  }

  replayResubscribeTasks () {
    Object.values(this.resubscribeTasks).forEach((task) => {
      task()
    })
  }

  cleanup () {
    this.events = {}
    this.sends = {}
    this.subIds = {}
    this.subPayloads = {}
    this.resubscribeTasks = {}
    this.ws = null
    this.url.value = ''
    this.hasOpened = false
    this.isManualClose = false
    this.scope?.stop()
  }

  close () {
    this.isManualClose = true
    this.closeRaw?.()
  }
}

export default BaseSocketClient
